import React, { useEffect, useState } from 'react';
import TrainingTileSidebar from './TrainingTileSidebar';
import UserMenu from '../../../components/UserMenu';
import AnnouncementBanner from '../../../components/announcements/AnnouncementBanner';
import { useAuth } from '../../../context/AuthContext';
import { TrainingLangProvider, useTrainingLang } from './i18n/TrainingLangContext';
import LanguageGate from './components/LanguageGate';
import LanguageSwitcher from './components/LanguageSwitcher';

const SESSION_SECONDS = 15 * 60;

const formatCountdown = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const expireTrainingSession = () => {
  const adminToken = localStorage.getItem('trainingKioskAdminToken');
  const adminUser = localStorage.getItem('trainingKioskAdminUser');
  if (adminToken && adminUser) {
    localStorage.setItem('token', adminToken);
    localStorage.setItem('user', adminUser);
  } else {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
  // Expired sessions land on the keyboard, not the prior trainee's confirm screen.
  sessionStorage.removeItem('kioskReturnUserId');
  window.location.href = '/tiles/labor_management_system/kiosk/kiosk-view';
};

const TrainingChrome = ({ children }) => {
  const { currentUser } = useAuth();
  const inTrainingKiosk = !!currentUser?.trainingKioskUser;
  const { t, hasChosen } = useTrainingLang();

  const [secondsLeft, setSecondsLeft] = useState(SESSION_SECONDS);
  const [extendedOnce, setExtendedOnce] = useState(false);

  useEffect(() => {
    if (!inTrainingKiosk) return undefined;
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          expireTrainingSession();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [inTrainingKiosk]);

  const handleExtend = () => {
    if (extendedOnce) return;
    setSecondsLeft(SESSION_SECONDS);
    setExtendedOnce(true);
  };

  // Nothing is shown until a language is picked — otherwise the first screen a
  // Spanish or Hmong speaker sees would be in English anyway. Regular users already
  // have a language on their profile and never see this; kiosk trainees pick once per
  // session because the tablet is shared.
  if (!hasChosen) return <LanguageGate />;

  const countdownColor = secondsLeft < 60 ? 'text-red-400' : 'text-slate-200';

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-slate-900 overflow-hidden">
      <TrainingTileSidebar />
      <div className="flex-1 w-full lg:w-auto lg:ml-64 flex flex-col h-screen overflow-hidden">
        <header className="bg-slate-800 border-slate-700 border-b flex-shrink-0">
          <div className="px-6 py-4 flex justify-between items-center gap-3">
            <h1 className="text-xl font-semibold text-slate-200 ml-12 lg:ml-0 min-w-0 truncate">
              {t('tileTitle')}
            </h1>
            <AnnouncementBanner />
            <div className="flex items-center gap-3 flex-shrink-0">
              <LanguageSwitcher />
              {inTrainingKiosk ? (
                <>
                  <span className="hidden sm:inline text-sm text-slate-300 bg-slate-700/60 px-3 py-1.5 rounded-md">
                    {currentUser?.full_name || currentUser?.username || 'Training Session'}
                  </span>
                  <span className={`text-sm font-mono font-semibold bg-slate-700/60 px-3 py-1.5 rounded-md ${countdownColor}`}>
                    {formatCountdown(secondsLeft)}
                  </span>
                  {!extendedOnce && (
                    <button
                      type="button"
                      onClick={handleExtend}
                      className="text-sm bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-md transition-colors"
                    >
                      {t('extend')}
                    </button>
                  )}
                </>
              ) : (
                <UserMenu isDarkMode={true} />
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto text-slate-100">
          {children}
        </main>
      </div>
    </div>
  );
};

const TrainingLayout = ({ children }) => (
  <TrainingLangProvider>
    <TrainingChrome>{children}</TrainingChrome>
  </TrainingLangProvider>
);

export default TrainingLayout;
